import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RastorLogo from './RastorLogo';
import { Shield, Zap, ArrowUp, Mail, Send, Github, Linkedin, Twitter, CheckCircle2 } from 'lucide-react';
import { StatusIndicator } from './TechUI';
import { newsletterAPI } from '../utils/api';
import authService from '../firebase/authService';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      setIsLoggedIn(!!user);
    });
    return () => unsubscribe();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setNewsletterStatus('loading');
    try {
      await newsletterAPI.subscribe(email);
      setNewsletterStatus('success');
      setEmail('');
      setTimeout(() => setNewsletterStatus('idle'), 4000);
    } catch (err) {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#08090c] border-t border-white/10 relative overflow-hidden">
      {/* Grid Background */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-5 space-y-6">
            <Link to="/" className="flex items-center space-x-3 group w-fit">
              <div className="w-10 h-10 border border-emerald-500/50 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                <RastorLogo size={22} />
              </div>
              <span className="text-xl font-mono font-bold text-white tracking-wider">
                RASTOR AI
              </span>
            </Link>

            <p className="text-slate-400 leading-relaxed max-w-md font-mono text-xs">
              {`> Military-grade career engineering platform.`}<br />
              {`> Deploy AI-powered growth strategies.`}<br />
              {`> Execute your promotion protocol.`}
            </p>

            <div className="flex flex-wrap gap-6 text-xs font-mono">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-slate-400 uppercase">256-bit Encrypted</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span className="text-slate-400 uppercase">Real-time Processing</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-4 pt-2">
              {[
                { icon: Twitter, href: 'https://twitter.com/rastorai', label: 'Twitter' },
                { icon: Linkedin, href: 'https://linkedin.com/company/rastorai', label: 'LinkedIn' },
                { icon: Github, href: 'https://github.com/rastorai', label: 'GitHub' },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-9 h-9 border border-white/10 bg-white/5 flex items-center justify-center hover:bg-emerald-500/20 hover:border-emerald-500/40 transition-all duration-300 group"
                >
                  <social.icon className="w-4 h-4 text-slate-400 group-hover:text-emerald-400 transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Links Grid */}
          <div className="lg:col-span-2 lg:col-start-7">
            <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest mb-6 border-l-2 border-emerald-500 pl-3">
              System
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Home', to: '/' },
                { label: 'Insights', to: '/insights' },
                { label: 'Dashboard', to: '/dashboard' },
                { label: 'Pricing', to: '/subscribe' }
              ].map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.to}
                    className="text-slate-400 hover:text-emerald-400 transition-colors text-xs font-mono uppercase tracking-wider flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 bg-slate-700 mr-3 group-hover:bg-emerald-500 transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest mb-6 border-l-2 border-blue-500 pl-3">
              Resources
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Blog', to: '/blog' },
                { label: 'Resume Builder', to: '/resume-builder' },
                { label: 'Explore Careers', to: '/careers' },
                { label: 'About Us', to: '/about' },
                { label: 'Contact Us', to: '/contact' }
              ].map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.to}
                    className="text-slate-400 hover:text-blue-400 transition-colors text-xs font-mono uppercase tracking-wider flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 bg-slate-700 mr-3 group-hover:bg-blue-500 transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter + Status Section */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest mb-6 border-l-2 border-purple-500 pl-3">
              Stay Updated
            </h4>

            {/* Inline Newsletter — only show form for non-logged-in visitors */}
            {isLoggedIn ? (
              <div className="mb-6 flex items-center gap-2 py-2.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs font-mono text-slate-400">You're subscribed to our newsletter</span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="mb-6">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 text-xs font-mono text-slate-300 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500/40 focus:bg-white/8 transition-all"
                      disabled={newsletterStatus === 'loading' || newsletterStatus === 'success'}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={newsletterStatus === 'loading' || newsletterStatus === 'success' || !email}
                    className="px-3 py-2.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
                {newsletterStatus === 'success' && (
                  <p className="text-emerald-400 text-[10px] font-mono mt-2 uppercase">✓ Subscribed successfully</p>
                )}
                {newsletterStatus === 'error' && (
                  <p className="text-red-400 text-[10px] font-mono mt-2 uppercase">✗ Failed. Try again.</p>
                )}
              </form>
            )}

            {/* System Status */}
            <div className="space-y-4 bg-white/5 border border-white/10 p-4">
              <StatusIndicator active label="API_OPERATIONAL" />
              <StatusIndicator active label="DATABASE_ONLINE" />
              <StatusIndicator active label="AI_MODELS_READY" />

              <div className="pt-4 border-t border-white/10">
                <div className="text-[10px] text-slate-500 uppercase font-mono mb-2">Uptime</div>
                <div className="text-xl font-mono font-bold text-white">99.98%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-slate-500 text-[10px] font-mono uppercase tracking-wider">
              © 2025 Rastor AI Inc. • All Systems Operational
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-6 text-[10px] text-slate-500 font-mono uppercase">
                <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy</Link>
                <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms</Link>
                <Link to="/refund-policy" className="hover:text-slate-300 transition-colors">Refund Policy</Link>
              </div>
              {/* Back to Top */}
              <button
                onClick={scrollToTop}
                className="w-8 h-8 border border-white/10 bg-white/5 flex items-center justify-center hover:bg-emerald-500/20 hover:border-emerald-500/40 transition-all duration-300 group"
                aria-label="Back to top"
              >
                <ArrowUp className="w-3.5 h-3.5 text-slate-400 group-hover:text-emerald-400 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
